import { Injectable, OnDestroy } from "@angular/core";
import { EventDataModel, EventDetailViewModel } from "../../shared/models/event.model";
import { Subscription, Subject, Observable } from "rxjs";
import { LikeService } from "../../services/like.service";
import { CoreLogService } from "../../services/log.service";
import { ContentType, ImageOrientation } from "../../shared/contentTypes";
import * as moment from "moment";
import { RatingService } from "../../services/rating.service";
import DateManager from "../../shared/dateManager";
import { environment } from "../../../../environments/environment";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { LocationService } from "../location/location.service";

@Injectable()
export class EventDetailService implements OnDestroy {

  private _eventDetailObsServer: IObjectObserver<EventDataModel>;
  private _eventDetailSupscription: Subscription;
  /** Subject to easy observe changes of event */
  private _dataChange: Subject<(string | number)[]> = new Subject<(string | number)[]>();

  constructor(private _likeService: LikeService,
    private _ratingService: RatingService,
    private _coreLogService: CoreLogService,
    private _locationService: LocationService,
    objObsInjectHelper: CircularInjectionHelper) {
    this._eventDetailObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<EventDataModel>(ContentType.Event, objObsInjectHelper);

    this._eventDetailSupscription = this._eventDetailObsServer.ObserveChanges.subscribe((keys) => {
      this._dataChange.next(keys);
    });

  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._dataChange.asObservable();
  }
  /**
    * Getting the eventDataModel by Id
    * Create eventViewModelList
    * @id id of Event
    * @result return a promise that resolves a  eventViewModel
    */
  public GetEventViewModel(id: number | string): Promise<EventDetailViewModel> {
    const me = this;
    return new Promise<EventDetailViewModel>((resolve, reject) => {
      // gets undeleted events
      me._eventDetailObsServer.GetObject(id)
        .then((eventDetail) => {
          me.mapDataModelToDetailViewModel(eventDetail).then(resolve)
            .catch((e) => {
              me._coreLogService.error("EventDetailService._eventDetailObsServer.GetObject", "mapDataModelToDetailViewModel", e);
              reject();
            });
        })
        .catch((e) => {
          me._coreLogService.error("EventDetailService._eventDetailObsServer.GetObject", "_eventDetailObsServer.GetObject", e);
          reject();
        });
    });
  }

  // Mapper

  /**
  * map a EventDataModel to EventViewModel
  * @param model Get a EventDataModel for mapping
  * @result return a EventViewModel
  */
  private async mapDataModelToDetailViewModel(model: EventDataModel): Promise<EventDetailViewModel> {
    const me = this;
    if (model) {
      const viewModel = new EventDetailViewModel();
      viewModel.id = model.id;
      viewModel.momentStartDate = model.date;
      viewModel.startDate = DateManager.MomentDateTimeFormatter(model.date, true, false);
      viewModel.startTime = DateManager.MomentDateTimeFormatter(model.date, false, true);
      viewModel.endDate = DateManager.MomentDateTimeFormatter(model.end_date, model.has_end_date_ymd, false);
      viewModel.endTime = DateManager.MomentDateTimeFormatter(model.end_date, false, model.has_end_date_hms);
      viewModel.label_de = model.label_de;
      viewModel.label_en = model.label_en;
      viewModel.text_de = model.text_de.replace("\n", "<br>");;
      viewModel.text_en = model.text_en.replace("\n", "<br>");;
      viewModel.image_photo = ContentType.Event + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
      try {
        viewModel.rate = await me._ratingService.GetRatingStars(ContentType.Event, model.id);
      } catch (error) {
        me._coreLogService.error("EventService.mapDataModelToViewModel", "GetRatingStars try catch", error);
      }
      try {
        viewModel.like = await me._likeService.HasLike(ContentType.Event, model.id);
      } catch (error) {
        me._coreLogService.error("EventService.mapDataModelToViewModel", "HasLike try catch", error);
      }
      try {
        viewModel.locations = await me._locationService.GetEventActionLocations(model.locations);
      } catch (error) {
        me._coreLogService.error("EventService.mapDataModelToViewModel", "_locationService.GetEventActionLocations try catch", error);

      }
      return Promise.resolve(viewModel);
    } else {
      return Promise.resolve(null);
    }
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._eventDetailSupscription) {
      me._eventDetailSupscription.unsubscribe();
    }
  }
}
